/**
 * A loading placeholder.
 *
 * It takes the SHAPE of what is coming rather than being a spinner, so the page
 * does not jump when the data lands. `aria-hidden` because a screen reader
 * should hear the loading state from the region's own `aria-busy`, not from a
 * row of empty boxes.
 *
 * WHY THE SHAPES BELOW EXIST. This file has held `Skeleton` all along and
 * almost nothing imported it: the screens spell `bg-skeleton-base h-4 w-full
 * animate-pulse rounded` inline instead, because a bulk replacement wrote the
 * literal classes at every site that used to carry a stylesheet's own skeleton
 * rule. Written as a literal, the same string landed on the container AND on
 * each row AND on each cell, three deep — and a 16px bar containing four 16px
 * bars containing three more renders as a single grey sliver that tells the
 * reader neither what is coming nor how much of it.
 *
 * A shape cannot make that mistake, because the nesting is inside the
 * component rather than at the call site.
 */

import { type ComponentProps } from "react"

import { cn } from "./cn"

export function Skeleton({ className, ...props }: ComponentProps<"div">) {
	return (
		<div
			aria-hidden="true"
			className={cn("rounded-control bg-skeleton-base animate-pulse", className)}
			{...props}
		/>
	)
}

/**
 * A paragraph's worth of lines.
 *
 * The last line is short, because that is what a last line looks like. Equal
 * full-width bars read as a table, which is the point of telling them apart.
 */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
	return (
		<div className={cn("flex flex-col gap-2", className)}>
			{Array.from({ length: lines }, (_, line) => (
				<Skeleton
					key={line}
					className={cn("h-4", line === lines - 1 ? "w-2/3" : "w-full")}
				/>
			))}
		</div>
	)
}

/**
 * A table or list, before it has any rows.
 *
 * The first column grows and the rest are fixed, which is the shape every list
 * screen in this product actually has — a title carrying the width, and short
 * columns beside it. `rows` and `cols` are the only things a caller varies.
 */
export function SkeletonRows({
	rows = 5,
	cols = 4,
	className,
}: {
	rows?: number
	cols?: number
	className?: string
}) {
	const widths = ["w-32", "w-20", "w-24", "w-16", "w-28"]

	return (
		<div className={cn("flex flex-col gap-3", className)}>
			{Array.from({ length: rows }, (_unusedRow, row) => (
				<div
					key={row}
					className="flex items-center gap-3"
				>
					<Skeleton className="h-4 flex-1" />
					{Array.from({ length: Math.max(0, cols - 1) }, (_unusedCol, col) => (
						<Skeleton
							key={col}
							className={cn("h-4 shrink-0", widths[col % widths.length])}
						/>
					))}
				</div>
			))}
		</div>
	)
}

/**
 * A chart, before it has any figures.
 *
 * Vertical bars of uneven height, because a chart's placeholder that is a
 * stack of horizontal lines sets the reader up for a table. The heights are a
 * fixed pattern rather than random so the placeholder does not flicker into a
 * different shape on every render.
 */
export function SkeletonChart({ bars = 7, className }: { bars?: number; className?: string }) {
	const heights = ["h-16", "h-24", "h-12", "h-32", "h-20", "h-28", "h-14"]

	return (
		<div className={cn("flex h-40 items-end gap-2", className)}>
			{Array.from({ length: bars }, (_, bar) => (
				<Skeleton
					key={bar}
					className={cn("flex-1", heights[bar % heights.length])}
				/>
			))}
		</div>
	)
}
